import { ReactNode, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

/**
 * EditorSectionCard – Groups multiple EditorSectionItem rows into a single card.
 *
 * Visual: white card, soft border, rounded-2xl, divide-y between items.
 * Use this to group related editor controls within a sidebar section.
 *
 * Canonical editor section UI – all Farmly editors MUST use this pattern.
 * See docs/ux/editor-layout.md for the full spec.
 */
interface EditorSectionCardProps {
  children: ReactNode;
  className?: string;
}

export function EditorSectionCard({ children, className }: EditorSectionCardProps) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-border/40 bg-card overflow-hidden divide-y divide-border/40',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * EditorSectionItem – A single collapsible row within an EditorSectionCard.
 *
 * Renders: icon + title + optional hint (collapsed) + chevron.
 * Expandable content slides open below.
 *
 * Interaction:
 * - Full header row is clickable
 * - Subtle hover state (bg-muted/30)
 * - Smooth chevron rotation
 * - Focus-visible ring
 * - Accessible button semantics + aria-expanded
 */
interface EditorSectionItemProps {
  /** Section title */
  title: string;
  /** Lucide icon component */
  icon: React.ElementType;
  /** Expandable content */
  children: ReactNode;
  /** Open by default */
  defaultOpen?: boolean;
  /** Hint text shown when collapsed (summary/status) */
  hint?: string;
  /** Controlled open state */
  open?: boolean;
  /** Controlled open change handler */
  onOpenChange?: (open: boolean) => void;
  /** data-testid for the trigger */
  testId?: string;
}

export function EditorSectionItem({
  title,
  icon: Icon,
  children,
  defaultOpen = false,
  hint,
  open: controlledOpen,
  onOpenChange,
  testId,
}: EditorSectionItemProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isOpen = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const handleOpenChange = (next: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={handleOpenChange}>
      <CollapsibleTrigger asChild>
        <button
          className={cn(
            'w-full text-left group',
            'flex items-center gap-2.5 px-4 py-3 cursor-pointer',
            'transition-colors duration-150 ease-out',
            'hover:bg-muted/30',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/30 focus-visible:ring-inset',
          )}
          data-testid={testId}
        >
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-foreground">{title}</span>
            {hint && !isOpen && (
              <p className="text-[11px] text-muted-foreground mt-0 truncate">{hint}</p>
            )}
          </div>
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform duration-200 ease-out',
              isOpen && 'rotate-180',
            )}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-4 pt-1">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}
